// Full-document render of the tempurpedic-only TRS order-history page
// (auth-required; the view redirects unauthenticated users to login before this
// renders): the breadcrumbs, an order-summary tracker island per order, the
// customer-service info block, and an empty-state message when no TRS orders are
// found.
import { Immutable } from "immer";
import React, { useState } from "react";
import { EmailVerificationGate } from "tsi-common-react/src/components/EmailVerificationGate/index";

import { templates } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { PageSkeleton } from "../components/PageSkeleton";
import { Template as CustomerServiceInfoBlock } from "./CustomerServiceInfoBlockTemplate";
import { Template as TrackOrderSummary } from "./TrackOrderSummaryTemplate";

type IVerificationPrompt = NonNullable<
    templates.UnifiedOrderHistoryDocumentTemplate["verification_prompt"]
>;

// Nothing was looked up, so offer the ownership check in place of the
// empty-state copy, which would otherwise tell the user we can't find orders we
// never went looking for. The gate emails a code on mount, so it stays behind a
// click: this page is where "Your Orders" lands every account without web
// orders, and a send-on-load would mail a code to anyone who merely visits.
const VerificationPrompt = (
    props: Immutable<{ prompt: IVerificationPrompt }>,
): React.JSX.Element => {
    const [isStarted, setIsStarted] = useState(false);
    if (isStarted) {
        return (
            <EmailVerificationGate
                email={props.prompt.email}
                instructions={props.prompt.gate_instructions}
                onVerified={() => {
                    getWindow()?.location.reload();
                }}
                onCancel={() => {
                    setIsStarted(false);
                }}
            />
        );
    }
    return (
        <div>
            <h3>{props.prompt.heading}</h3>
            <p>{props.prompt.intro}</p>
            <button
                type="button"
                className="button"
                onClick={() => {
                    setIsStarted(true);
                }}
            >
                {props.prompt.cta_label}
            </button>
        </div>
    );
};

export const Template = (
    props: templates.UnifiedOrderHistoryDocumentTemplate,
): React.JSX.Element => {
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="l-capped-width">
                <section>
                    <div>
                        <nav className="unified-order-breadcrumbs">
                            {props.crumbs.map((crumb, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && " / "}
                                    {crumb.href ? (
                                        <a href={crumb.href}>{crumb.label}</a>
                                    ) : (
                                        <span>{crumb.label}</span>
                                    )}
                                </React.Fragment>
                            ))}
                        </nav>
                        <h2>{props.heading}</h2>
                        {props.verification_prompt ? (
                            <VerificationPrompt
                                prompt={props.verification_prompt}
                            />
                        ) : (
                            props.order_summaries.length === 0 && (
                                <div>
                                    <p>
                                        {props.empty_message}
                                        <a
                                            className="unified-order-link"
                                            href={props.lookup_url}
                                        >
                                            {props.here_label}
                                        </a>
                                    </p>
                                </div>
                            )
                        )}
                        <div>
                            {props.order_summaries.map((summary, i) => (
                                <TrackOrderSummary key={i} {...summary} />
                            ))}
                        </div>
                    </div>
                </section>
                <CustomerServiceInfoBlock {...props.cs_block} />
            </div>
        </PageSkeleton>
    );
};
